import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CustomButton from '../../../components/common/CustomButton';

export function Feedbacks() {
  const { palette, typography, breakpoints } = useTheme();
  const { t } = useTranslation('landing');
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'));
  return (
    <Box id="feedbacks" component="section">
      <Container
        sx={{
          pt: { xs: 4, sm: 12 },
          pb: { xs: 8, sm: 16 },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 3, sm: 6 },
        }}
      >
        <Box
          sx={{
            width: { sm: '100%', md: '80%' },
            textAlign: { sm: 'left', md: 'center' },
            py: 2,
          }}
        >
          <Typography
            gutterBottom
            component="h2"
            sx={{
              fontSize: {
                xs: typography.h3.fontSize,
                md: typography.h1.fontSize,
              },
              fontWeight: {
                md: typography.h1.fontWeight,
              },
              color: palette.primary.main,
              pb: 2,
            }}
          >
            {t('landing:feedbacks.title')}
          </Typography>
          <Typography
            gutterBottom
            sx={{
              fontSize: {
                xs: typography.body1.fontSize,
                md: typography.h4.fontSize,
              },
              color: palette.text.secondary,
            }}
          >
            {t('landing:feedbacks.description')}
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex' }}>
            <Card
              variant="outlined"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                borderRadius: 3,
                flexGrow: 1,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ color: 'text.secondary' }}
                >
                  {t('landing:feedbacks.feedbackOne.description')}
                </Typography>
              </CardContent>
              <Typography p={2} fontStyle="italic">
                {t('landing:feedbacks.feedbackOne.givenBy')}
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex' }}>
            <Card
              variant="outlined"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flexGrow: 1,
                borderRadius: 3,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ color: 'text.secondary' }}
                >
                  {t('landing:feedbacks.feedbackTwo.description')}
                </Typography>
              </CardContent>
              <Typography p={2} fontStyle="italic">
                {t('landing:feedbacks.feedbackTwo.givenBy')}
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex' }}>
            <Card
              variant="outlined"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flexGrow: 1,
                borderRadius: 3,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ color: 'text.secondary' }}
                >
                  {t('landing:feedbacks.feedbackThree.description')}
                </Typography>
              </CardContent>
              <Typography p={2} fontStyle="italic">
                {t('landing:feedbacks.feedbackThree.givenBy')}
              </Typography>
            </Card>
          </Grid>
        </Grid>
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              py: 2,
              mt: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h3"
              sx={{
                fontSize: {
                  xs: typography.h4.fontSize,
                  md: typography.h2.fontSize,
                },
                fontWeight: {
                  xs: typography.h4.fontWeight,
                  md: typography.h1.fontWeight,
                },
                color: palette.text.primary,
              }}
            >
              Don't let another story be lost
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: {
                  xs: typography.body1.fontSize,
                  md: typography.h4.fontSize,
                },
                fontWeight: {
                  xs: typography.body2.fontWeight,
                  md: typography.body1.fontWeight,
                },
                color: palette.text.secondary,
              }}
            >
              Every day that passes is a story that gets harder to tell. Start
              with one person. Start today.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifySelf: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CustomButton
              label="Begin Your Legacy - Free"
              color="info"
              onClick={() => navigate('/auth/signup')}
              size={isSmallScreen ? 'small' : 'medium'}
              sx={{
                fontSize: {
                  xs: '0.8rem',
                  sm: '0.9rem',
                  md: typography.body1.fontSize,
                },
                color: palette.primary.contrastText,
                width: { xs: '100%', sm: 'auto' },
                whiteSpace: 'nowrap',
                textAlign: 'center',
                '&:hover': {
                  color: 'white',
                },
              }}
            />
            <Typography variant="caption">
              No credit card required • Start in 2 minutes
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
